import React, { useEffect, useState } from "react";
import oraclePhase27Api from "@/api/oraclePhase27Client";
import ComplianceLock from "@/components/compliance/ComplianceLock";

/**
 * CareLogix™ ORACLE — ORACLE SCORE™ card (Phase 27C).
 *
 * Renders the composite 0–100 ORACLE SCORE with 9 sub-factor breakdown, each
 * showing reason / source / confidence / uncertainty / timestamp.
 */
const VERDICT_COLORS = {
  watch_strong: "#10b981",
  watch:        "#22c55e",
  consider:     "#fcd34d",
  hold:         "#94a3b8",
  avoid:        "#ef4444",
};

const DIR_COLORS = {
  bullish: "#10b981",
  neutral: "#c5a059",
  bearish: "#ef4444",
};

function ProgressBar({ value, hue = "#c5a059", testid }) {
  const v = Math.max(0, Math.min(100, Number(value || 0)));
  return (
    <div style={S.bar} data-testid={testid}>
      <div style={{ ...S.barFill, width: `${v}%`, background: `linear-gradient(90deg, ${hue}55, ${hue})` }} />
    </div>
  );
}

function SubScoreRow({ s }) {
  return (
    <div style={S.subRow} data-testid={`oracle-score-sub-${s.id}`}>
      <div style={S.subHead}>
        <strong style={{ color: "#fde68a" }}>{s.label}</strong>
        <span style={S.subScore}>{s.score}</span>
      </div>
      <ProgressBar value={s.score} hue={s.score >= 65 ? "#10b981" : (s.score >= 45 ? "#c5a059" : "#ef4444")} />
      <p style={S.subReason}>“{s.reason}”</p>
      <div style={S.subMeta}>
        <span><b>SRC</b> {s.source_type}</span>
        <span><b>CONF</b> {s.confidence}%</span>
        <span><b>UNC</b> {s.uncertainty}%</span>
      </div>
    </div>
  );
}

export default function OracleScoreCard({
  symbol = "AAPL",
  assetClass = "stocks",
  compact = false,
  testid = "oracle-score-card",
}) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    setBusy(true); setErr("");
    oraclePhase27Api.score(symbol, assetClass)
      .then(({ data }) => { if (alive) setData(data); })
      .catch((e) => { if (alive) setErr(e?.message || "score_failed"); })
      .finally(() => { if (alive) setBusy(false); });
    return () => { alive = false; };
  }, [symbol, assetClass]);

  if (busy && !data) return <div style={S.shell} data-testid={`${testid}-loading`}>Loading ORACLE SCORE…</div>;
  if (err) return <div style={S.shell} data-testid={`${testid}-error`}>⚠ {err}</div>;
  if (!data) return null;

  const dirColor = DIR_COLORS[data.direction] || DIR_COLORS.neutral;
  const verdictColor = VERDICT_COLORS[data.verdict] || VERDICT_COLORS.consider;

  return (
    <section style={S.shell} data-testid={testid}>
      <header style={S.head}>
        <div style={S.headLeft}>
          <span style={S.tagline}>ORACLE SCORE™</span>
          <h3 style={S.title}>{data.symbol} · <small style={{ color: "#94a3b8" }}>{data.asset_class}</small></h3>
        </div>
        <div style={{ ...S.scoreBig, color: verdictColor }} data-testid={`${testid}-composite`}>
          {data.oracle_score}
          <span style={S.scoreSlash}>/100</span>
        </div>
      </header>

      <div style={S.summaryRow}>
        <span style={{ ...S.chip, color: dirColor, borderColor: dirColor }} data-testid={`${testid}-direction`}>
          {(data.direction || "").toUpperCase()}
        </span>
        <span style={{ ...S.chip, color: verdictColor, borderColor: verdictColor }} data-testid={`${testid}-verdict`}>
          {data.verdict_label}
        </span>
        <span style={S.metaChip}>Conf avg <b>{data.meta?.mean_confidence}%</b></span>
        <span style={S.metaChip}>Unc avg <b>{data.meta?.mean_uncertainty}%</b></span>
        <span style={S.metaChip}>HITL <b>Required</b></span>
      </div>

      {!compact && (
        <div style={S.subGrid}>
          {data.sub_scores.map((s) => <SubScoreRow key={s.id} s={s} />)}
        </div>
      )}

      <ComplianceLock variant="inline" short policy="v27" testid={`${testid}-compliance`} />
    </section>
  );
}

const S = {
  shell: {
    background: "rgba(11,15,20,0.92)",
    border: "1px solid rgba(197,160,89,0.20)",
    borderRadius: 14,
    padding: 14,
    color: "#e2e8f0",
    fontFamily: "'Figtree', 'Inter', system-ui, sans-serif",
    display: "flex",
    flexDirection: "column",
    gap: 10,
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
  },
  head: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" },
  headLeft: { display: "flex", flexDirection: "column", gap: 2 },
  tagline: { color: "#c5a059", fontFamily: "'Space Mono', monospace", fontSize: 10.5, letterSpacing: "0.18em" },
  title: { color: "#fde68a", fontSize: 17, margin: 0 },
  scoreBig: { fontFamily: "'Space Mono', monospace", fontSize: 38, fontWeight: 900, letterSpacing: "-0.03em" },
  scoreSlash: { fontSize: 14, color: "#94a3b8", marginLeft: 4 },
  summaryRow: { display: "flex", flexWrap: "wrap", gap: 6 },
  chip: {
    fontFamily: "'Space Mono', monospace",
    fontSize: 10.5,
    fontWeight: 800,
    letterSpacing: "0.08em",
    padding: "3px 9px",
    border: "1px solid",
    borderRadius: 999,
  },
  metaChip: {
    color: "#cbd5e1",
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.06)",
    padding: "3px 9px",
    fontSize: 11,
    borderRadius: 999,
    fontFamily: "'Space Mono', monospace",
  },
  subGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 8, marginTop: 4 },
  subRow: { background: "#040608", border: "1px solid rgba(255,255,255,0.04)", borderRadius: 10, padding: 9, display: "flex", flexDirection: "column", gap: 4 },
  subHead: { display: "flex", justifyContent: "space-between", alignItems: "baseline" },
  subScore: { color: "#c5a059", fontFamily: "'Space Mono', monospace", fontSize: 16, fontWeight: 800 },
  bar: { height: 4, background: "rgba(255,255,255,0.06)", borderRadius: 4, overflow: "hidden" },
  barFill: { height: "100%", borderRadius: 4, transition: "width 240ms ease" },
  subReason: { color: "#cbd5e1", fontStyle: "italic", fontSize: 11, lineHeight: 1.4, margin: 0 },
  subMeta: { display: "flex", flexWrap: "wrap", gap: 7, color: "#64748b", fontFamily: "'Space Mono', monospace", fontSize: 9.5, letterSpacing: "0.06em" },
};
